import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Briefcase } from 'lucide-react';

import { bookingService } from '@/services/api/booking.service';
import { useUpdateUser } from '@/hooks/use-users';
import { useToast } from '@/hooks/use-toast';
import { sanitizeErrorMessage } from '@/lib/error-utils';

import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

import type { Provider, Service } from '@/types/booking';
import type { User } from '@/types/auth';

const editMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
  specialization: z.string().max(100).optional(),
  bio: z.string().max(500).optional(),
  serviceIds: z.array(z.number()).optional(),
  providerActive: z.boolean().optional(),
});

type EditMemberFormData = z.infer<typeof editMemberSchema>;

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: User | null;
  provider: Provider | null | undefined;
  services: Service[];
  onSaved?: () => void;
}

export function EditMemberDialog({
  open,
  onOpenChange,
  member,
  provider,
  services,
  onSaved,
}: EditMemberDialogProps) {
  const { t } = useTranslation(['team', 'common', 'providers']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const updateUser = useUpdateUser();

  const updateProviderMutation = useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<Provider> }) =>
      bookingService.updateProvider(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-team'] });
      queryClient.invalidateQueries({ queryKey: ['admin-team-member'] });
    },
    onError: (error: unknown) => {
      toast({
        title: t('common:toasts.error'),
        description: sanitizeErrorMessage(error, t('providers:toasts.updateError')),
        variant: 'destructive',
      });
    },
  });

  const form = useForm<EditMemberFormData>({
    resolver: zodResolver(editMemberSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      specialization: '',
      bio: '',
      serviceIds: [],
      providerActive: true,
    },
  });

  useEffect(() => {
    if (open && member) {
      form.reset({
        firstName: member.firstName,
        lastName: member.lastName,
        phone: member.phone || '',
        specialization: provider?.specialization || '',
        bio: provider?.bio || '',
        serviceIds: provider?.services?.map((s) => s.id) || [],
        providerActive: provider?.active ?? true,
      });
    }
  }, [open, member, provider, form]);

  const handleSubmit = async (data: EditMemberFormData) => {
    if (!member) return;

    await updateUser.mutateAsync({
      id: member.id,
      updates: {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || undefined,
      },
    });

    if (provider) {
      await updateProviderMutation.mutateAsync({
        id: provider.id,
        updates: {
          displayName: `${data.firstName} ${data.lastName}`,
          specialization: data.specialization,
          bio: data.bio,
          serviceIds: data.serviceIds,
          active: data.providerActive ?? true,
          userId: provider.userId || 0,
          organizationId: provider.organizationId || 0,
        },
      });
    }

    onOpenChange(false);
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('team:editMember.title', 'Edit Team Member')}</DialogTitle>
          <DialogDescription>
            {t('team:editMember.description', 'Update team member details')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:editMember.firstName', 'First Name')}</FormLabel>
                  <FormControl>
                    <Input className="h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:editMember.lastName', 'Last Name')}</FormLabel>
                  <FormControl>
                    <Input className="h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('team:editMember.phone', 'Phone')}</FormLabel>
                  <FormControl>
                    <Input className="h-12" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {provider && (
              <>
                <Separator className="my-4" />
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  {t('team:editMember.providerDetails', 'Provider Details')}
                </h4>

                <FormField
                  control={form.control}
                  name="specialization"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('providers:form.specialization', 'Specialization')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('providers:form.specializationPlaceholder', 'e.g., Hair Stylist')}
                          className="h-12"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bio"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('providers:form.bio', 'Bio')}</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={t('providers:form.bioPlaceholder', 'Brief description...')}
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="serviceIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center justify-between">
                        <span>{t('providers:form.services', 'Services Offered')}</span>
                        <Badge variant="secondary" className="text-xs">
                          {t('providers:form.selected', { count: field.value?.length || 0 })}
                        </Badge>
                      </FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={services
                            .filter((s) => s.active)
                            .map((s) => ({
                              label: s.name,
                              value: s.id,
                              description: s.duration ? `${s.duration} min` : undefined,
                            }))}
                          value={field.value || []}
                          onChange={field.onChange}
                          placeholder={t('providers:form.selectServices', 'Select services...')}
                          emptyMessage={t('providers:form.noServices', 'No services available')}
                        />
                      </FormControl>
                      <p className="text-xs text-slate-500">
                        {t('providers:form.servicesHint', 'Select which services this provider can perform')}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="providerActive"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between">
                      <FormLabel>{t('providers:form.activeProvider', 'Active Provider')}</FormLabel>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="h-11"
              >
                {t('common:actions.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={updateUser.isPending} className="h-11">
                {updateUser.isPending
                  ? t('common:actions.saving', 'Saving...')
                  : t('common:actions.save', 'Save')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
